/**
 * AviHeader — Bandeau haut du module AVI (2 bandes).
 *
 * Spec iter174 (2026-02-27) — extrait de `pages/Avi.jsx` (bloc header)
 * pour respecter la règle des ~700 lignes. AUCUN changement de
 * comportement — pure réorganisation de code.
 *
 * Structure :
 *   • Bande 1 (fond #191919) : logo + titre + badge compteur + toggle
 *     Perso/Globale + Corbeille + HeaderProfileGroup
 *   • Bande 2 (fond #1E1E1E) : onglets Validation / Accusé / Information
 */

import {
  Globe2, UserCircle2, AlertTriangle, CheckCheck, Trash2, Clock, Settings2,
} from "lucide-react";
import { AVI_SUBTYPES } from "@/components/icons/AviSubtypeIcons";
import AviModuleLogo from "@/components/icons/AviModuleLogo";
import ScopeSwitch from "@/components/ui/ScopeSwitch";
import HeaderPill from "@/components/ui/HeaderPill";
import HeaderProfileGroup from "@/components/TopBar";

const TAB_CSS = `.avi-tab{flex:1;min-width:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;font-size:12px;padding:7px 8px;white-space:nowrap;color:#c8c8cf;border-bottom:2px solid transparent;margin-bottom:-1px;background:none;cursor:pointer;transition:color .15s,border-color .15s}
.avi-tab>svg{width:40px;height:40px}
.avi-tab:hover{color:#e8e8ec}.avi-tab[data-active="true"]{font-weight:500}.avi-tab[data-disabled="true"]{cursor:not-allowed;opacity:.55}
.avi-tab-sep{width:1px;height:22px;background:#26262b;align-self:center;flex-shrink:0}`;

export default function AviHeader({
  subtype, setSubtype,
  meta, viewScope, setViewScope,
  summary,
  unreadConversationCount, onMarkAllRead,
  canAdmin, onOpenTrash,
  canConfig, onOpenConfig,
}) {
  return (
    <div
      style={{ border: "1px solid #2A2A2A", borderRadius: 12, overflow: "hidden" }}
      data-testid="avi-header-bar"
    >
      {/* Bande 1 : bandeau titre (plus sombre) */}
      <div
        className="flex items-center"
        style={{ background: "#191919", padding: "10px 16px" }}
        data-testid="avi-header-title-band"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 shrink-0">
            <AviModuleLogo subtype={subtype} size={42} />
            <div className="flex flex-col min-w-0">
              <span
                className="font-bold flex items-center gap-2 flex-wrap"
                style={{ fontSize: 16, color: "#EDEDED", lineHeight: 1.15 }}
                data-testid="avi-header-title"
              >
                {meta.label}
                {viewScope === "global" ? (
                  <Globe2 size={15} strokeWidth={2.2} style={{ color: "#3B9CE0" }} data-testid="avi-scope-icon-global">
                    <title>Vue globale — toutes les demandes de l'organisation</title>
                  </Globe2>
                ) : (
                  <UserCircle2 size={15} strokeWidth={2.2} style={{ color: "#3B9CE0" }} data-testid="avi-scope-icon-mine">
                    <title>Vue personnelle — vos demandes</title>
                  </UserCircle2>
                )}
                <span
                  data-testid="avi-header-count-badge"
                  style={{
                    background: subtype === "validation" ? "#5B5FC7" : meta.color,
                    color: "#FFFFFF",
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "3px 11px",
                    borderRadius: 999,
                    lineHeight: 1.1,
                    letterSpacing: "0.02em",
                  }}
                >
                  {(() => {
                    // Spec iter180 pt.5 (2026-02-27) — sur Information, le
                    // badge à côté du titre ne compte QUE les infos en
                    // attente de lecture complète (`en_attente` +
                    // `partiel_repondu`), pas le total créé. Revient à 0
                    // dès que toutes les infos sont lues par leurs
                    // destinataires (ou clôturées). Validation et Accusé
                    // conservent le total (comportement historique).
                    if (subtype === "information") {
                      const pending = (summary.waiting || 0) + (summary.partial || 0);
                      return `${pending} en attente`;
                    }
                    return `${summary.total} demande${summary.total > 1 ? "s" : ""}`;
                  })()}
                </span>
                {viewScope === "global" && (
                  <span
                    data-testid="avi-header-waiting-badge"
                    className="inline-flex items-center gap-1"
                    style={{
                      background: "#3a1414",
                      color: "#e07a7a",
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "3px 11px",
                      borderRadius: 999,
                      lineHeight: 1.1,
                      letterSpacing: "0.02em",
                    }}
                  >
                    <AlertTriangle size={11} strokeWidth={2.4} />
                    {(summary.waiting ?? 0)} en attente
                  </span>
                )}
              </span>
              <span
                className="text-muted-foreground"
                style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.2 }}
                data-testid="avi-header-tagline"
              >
                {meta.tagline}
              </span>
            </div>
          </div>
        </div>

        {/* Bloc droite : Vue globale + Corbeille + HeaderProfileGroup */}
        <div className="flex items-center gap-2 shrink-0">
          {unreadConversationCount > 0 && (
            <button
              type="button"
              onClick={onMarkAllRead}
              className="inline-flex items-center gap-1.5 font-semibold transition-all"
              style={{
                height: 28, padding: "0 10px",
                borderRadius: 16, fontSize: 11,
                background: "#0F2A55", border: "1px solid #3B82F6", color: "#93C5FD",
              }}
              title="Marquer toutes les conversations comme lues"
              data-testid="avi-mark-all-read"
            >
              <CheckCheck size={12} />
              Tout marquer comme lu
              <span className="ml-1 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold text-white flex items-center justify-center"
                    style={{ background: "#3B82F6" }}>
                {unreadConversationCount}
              </span>
            </button>
          )}
          <ScopeSwitch
            module="avi"
            subtype={subtype}
            value={viewScope}
            onChange={(next) => setViewScope(next)}
            testid="avi-header-scope"
          />
          {canConfig && (
            <HeaderPill
              icon={Settings2} iconBg="#E0822E" label="Configuration"
              onClick={onOpenConfig}
              title="Configuration du module AVI — clôture automatique"
              testid="avi-header-config"
            />
          )}
          {canAdmin && (
            <HeaderPill
              icon={Trash2} iconBg="#EF4444" label="Corbeille"
              onClick={onOpenTrash}
              title="Corbeille — demandes supprimées"
              testid="avi-header-trash"
            />
          )}
          <div className="ml-3">
            <HeaderProfileGroup />
          </div>
        </div>
      </div>{/* /bande1 */}

      {/* Bande 2 : onglets sous-types (fond plus clair) */}
      <style>{TAB_CSS}</style>
      <div style={{ background: "#1E1E1E" }} data-testid="avi-header-tabs-band">
        <nav className="avi-tabs flex items-stretch" style={{ width: "100%", borderBottom: "0.5px solid #26262b" }} data-testid="avi-type-selector">
          {Object.values(AVI_SUBTYPES).map((s, i) => {
            const active = s.key === subtype;
            const disabled = !s.enabled;
            const SubIcon = s.Icon;
            return (
              <span key={s.key} className="flex items-stretch" style={{ flex: 1, minWidth: 0 }}>
                {i > 0 && <span className="avi-tab-sep" data-testid="avi-tab-sep" />}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => !disabled && setSubtype(s.key)}
                  data-testid={`avi-subtype-${s.key}`}
                  className="avi-tab"
                  data-active={active}
                  data-disabled={disabled || undefined}
                  style={active ? { color: s.color, borderBottomColor: s.color } : undefined}
                  title={disabled ? "Ce type arrive bientôt — reste à l'écoute !" : s.label}
                >
                  <SubIcon size={40} style={{ width: 40, height: 40 }} />
                  <span className="inline-flex items-center gap-1">
                    {s.label}
                    {disabled && <Clock size={12} strokeWidth={2.2} />}
                  </span>
                </button>
              </span>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
